// Scoreboard building blocks shared by the Tab scoreboard and the end-of-match overlay.

import { div, setClass, span } from "./dom";
import { collectSortedRows, pingTier, teamName } from "./format";
import type { BotLevel, ScoreboardRow, TeamTag } from "./types";

interface RowView {
  root: HTMLDivElement;
  name: HTMLSpanElement;
  k: HTMLSpanElement;
  d: HTMLSpanElement;
  a: HTMLSpanElement;
  score: HTMLSpanElement;
  obj: HTMLSpanElement;
  ping: HTMLSpanElement;
  // cache
  nameText: string;
  bot: BotLevel | null;
  team: TeamTag | "";
  kills: number;
  deaths: number;
  assists: number;
  scoreN: number;
  objN: number;
  pingN: number;
  tier: number;
  me: boolean;
  alive: boolean;
}

const HEAD: readonly [string, string][] = [
  ["name", "PLAYER"],
  ["k", "K"],
  ["d", "D"],
  ["a", "A"],
  ["score", "SCORE"],
  ["obj", "OBJ"],
  ["ping", "PING"],
];

/** One table of player rows. Row nodes are pooled; cells are written only when their value changed. */
export class ScoreTable {
  readonly root = div("hud-st");
  private readonly body = div("hud-st__body");
  private readonly views: RowView[] = [];
  private shown = 0;

  constructor() {
    const head = div("hud-st__row hud-st__head");
    for (const [cls, label] of HEAD) head.append(span(`hud-st__c hud-st__${cls}`, label));
    this.root.append(head, this.body);
  }

  /** Show the objective column (KOTH hill time, Defusal plants / defuses). */
  setObjectiveColumn(on: boolean): void {
    setClass(this.root, "has-obj", on);
  }

  /** `rows` must already be sorted. */
  render(rows: readonly ScoreboardRow[]): void {
    const n = rows.length;
    for (let i = 0; i < n; i++) {
      const v = this.views[i] ?? this.grow();
      if (i >= this.shown) v.root.hidden = false;
      this.fill(v, rows[i]!);
    }
    for (let i = n; i < this.shown; i++) this.views[i]!.root.hidden = true;
    this.shown = n;
  }

  private grow(): RowView {
    const root = div("hud-st__row");
    const v: RowView = {
      root,
      name: span("hud-st__c hud-st__name"),
      k: span("hud-st__c hud-st__k"),
      d: span("hud-st__c hud-st__d"),
      a: span("hud-st__c hud-st__a"),
      score: span("hud-st__c hud-st__score"),
      obj: span("hud-st__c hud-st__obj"),
      ping: span("hud-st__c hud-st__ping"),
      nameText: "",
      bot: null,
      team: "",
      kills: NaN,
      deaths: NaN,
      assists: NaN,
      scoreN: NaN,
      objN: NaN,
      pingN: NaN,
      tier: -1,
      me: false,
      alive: true,
    };
    root.append(v.name, v.k, v.d, v.a, v.score, v.obj, v.ping);
    this.body.append(root);
    this.views.push(v);
    return v;
  }

  private fill(v: RowView, r: ScoreboardRow): void {
    if (r.name !== v.nameText || r.bot !== v.bot) {
      v.nameText = r.name;
      v.bot = r.bot;
      v.name.textContent = r.name;
      if (r.bot !== null) v.name.append(span(`hud-st__bot hud-st__bot--${r.bot}`, `BOT ${r.bot.toUpperCase()}`));
    }
    if (r.team !== v.team) {
      v.team = r.team;
      v.root.dataset.team = r.team;
    }
    if (r.kills !== v.kills) {
      v.kills = r.kills;
      v.k.textContent = String(r.kills);
    }
    if (r.deaths !== v.deaths) {
      v.deaths = r.deaths;
      v.d.textContent = String(r.deaths);
    }
    if (r.assists !== v.assists) {
      v.assists = r.assists;
      v.a.textContent = String(r.assists);
    }
    if (r.score !== v.scoreN) {
      v.scoreN = r.score;
      v.score.textContent = String(r.score);
    }
    if (r.objective !== v.objN) {
      v.objN = r.objective;
      v.obj.textContent = String(r.objective);
    }
    const ping = Math.round(r.pingMs);
    if (ping !== v.pingN) {
      v.pingN = ping;
      v.ping.textContent = String(ping);
      const tier = pingTier(ping);
      if (tier !== v.tier) {
        v.tier = tier;
        v.ping.dataset.tier = String(tier);
      }
    }
    if (r.isMe !== v.me) {
      v.me = r.isMe;
      setClass(v.root, "is-me", r.isMe);
    }
    if (r.alive !== v.alive) {
      v.alive = r.alive;
      setClass(v.root, "is-dead", !r.alive);
    }
  }
}

class TeamBlock {
  readonly root = div("hud-sb__team");
  readonly table = new ScoreTable();
  private readonly nameEl = span("hud-sb__teamname");
  private readonly scoreEl = span("hud-sb__teamscore");
  private readonly head = div("hud-sb__teamhead");
  private team: TeamTag | "" = "";
  private nameText = "";
  private scoreText = "";

  constructor() {
    this.head.append(this.nameEl, this.scoreEl);
    this.root.append(this.head, this.table.root);
  }

  setHeader(team: TeamTag, score: number | null, visible: boolean): void {
    this.head.hidden = !visible;
    if (team !== this.team) {
      this.team = team;
      this.root.dataset.team = team;
    }
    const name = teamName(team);
    if (name !== this.nameText) {
      this.nameText = name;
      this.nameEl.textContent = name;
    }
    const s = score === null ? "" : String(score);
    if (s !== this.scoreText) {
      this.scoreText = s;
      this.scoreEl.textContent = s;
    }
  }
}

/**
 * FFA: one table. Team modes: the local player's team on the left, the other on the right, each with a
 * header (team name and score).
 */
export class ScoreLayout {
  readonly root = div("hud-sb__cols");
  private readonly left = new TeamBlock();
  private readonly right = new TeamBlock();
  private readonly sortedL: ScoreboardRow[] = [];
  private readonly sortedR: ScoreboardRow[] = [];

  constructor() {
    this.root.append(this.left.root, this.right.root);
  }

  /** Returns the team shown on the left (the local player's team; "a" for FFA or when unknown). */
  render(rows: readonly ScoreboardRow[], teams: boolean, teamScores: readonly [number, number] | null, objective: boolean): "a" | "b" {
    setClass(this.root, "is-teams", teams);
    this.left.table.setObjectiveColumn(objective);
    this.right.table.setObjectiveColumn(objective);
    if (!teams) {
      this.right.root.hidden = true;
      this.left.setHeader("none", null, false);
      this.left.table.render(collectSortedRows(rows, "all", this.sortedL));
      return "a";
    }
    let meTeam: "a" | "b" = "a";
    for (let i = 0; i < rows.length; i++) {
      if (rows[i]!.isMe) {
        meTeam = rows[i]!.team === "b" ? "b" : "a";
        break;
      }
    }
    const other: "a" | "b" = meTeam === "a" ? "b" : "a";
    this.right.root.hidden = false;
    this.left.setHeader(meTeam, teamScores ? teamScores[meTeam === "a" ? 0 : 1] : null, true);
    this.right.setHeader(other, teamScores ? teamScores[other === "a" ? 0 : 1] : null, true);
    this.left.table.render(collectSortedRows(rows, meTeam, this.sortedL));
    this.right.table.render(collectSortedRows(rows, other, this.sortedR));
    return meTeam;
  }
}
